import { ReactElement, Suspense } from 'react'
import Loader from 'authorization-module/lib/ui/atoms/Loader'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import routes from 'const/routes'
import {
  useAccountFeatures,
  useAccountSummary,
  useCreditAccounts
} from 'const/syfApiQueries'
import findAccount from 'helpers/findAccount'
import shouldShowAccountServiceButton from 'pages/AccountServices/shouldShowAccountServiceButton'
import PaymentSectionHeader from 'pages/PageLayout/PaymentSectionHeader'
import useWindowSize from 'syf-component-library/ui/customHooks/useWindowWidth'
import ErrorBoundary from 'ui/patterns/ErrorBoundary'

import { useAtomValue } from 'jotai'
import { selectedAccountAtom } from 'store/atoms'
import MenuOptions from './MenuOptions'
import { NavigationContainer, PageWrapper } from './subcomponents'

const PageLayout = (): ReactElement => {
  const selectedAccount = useAtomValue(selectedAccountAtom)

  const windowWidth = useWindowSize()
  const location = useLocation()

  const { data: { accounts = [] } = {}, isError: isCreditError } =
    useCreditAccounts()

  const {
    data: accountSummary,
    isLoading,
    isError: isAccountSummaryError
  } = useAccountSummary(selectedAccount)
  const { data: accountFeatures } = useAccountFeatures(selectedAccount)

  if (isAccountSummaryError || isCreditError) {
    return <Navigate to={routes.ERROR} />
  }

  const account = findAccount(accounts, selectedAccount)
  const showAccountServiceMenu =
    accountFeatures?.accountServicingEnabled &&
    shouldShowAccountServiceButton(accountSummary)

  return (
    <>
      <PaymentSectionHeader
        isLoading={isLoading}
        account={account}
        accountSummary={accountSummary}
      />
      <NavigationContainer>
        {!windowWidth.isLessThanMobile && (
          <MenuOptions
            showAccountServiceMenu={showAccountServiceMenu}
            activeLink={location.pathname}
            accountId={selectedAccount}
          />
        )}

        <PageWrapper>
          <ErrorBoundary>
            <Suspense fallback={<Loader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </PageWrapper>
      </NavigationContainer>
    </>
  )
}

export default PageLayout
